import type { CSSProperties } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface EventCardProps {
  title: string;
  timeRange: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Card de evento do hero — a placa fina do calendário na pele escura da home.
 *
 * Nasceu como desenho chapado dentro de `group-stickers-green-collors.svg`
 * (texto vetorizado em `<path>` + quatro filtros gaussianos). Naquele formato o
 * navegador rasterizava a camada inteira no tamanho de layout e depois esticava
 * o bitmap pelo transform 3D do hero — daí o borrão e a aresta serrilhada.
 * Em DOM o texto é texto e a sombra é `box-shadow` de cada card, então nada
 * passa por uma textura ampliada.
 *
 * Medidas herdadas do SVG original: corpo `#1D1D1D`, faixa `#DEF771`, sombra
 * `dy 3.45 / blur σ5.6`, raio 14px. O que mudou de propósito é o trilho — era
 * um bloco arredondado de 18px, virou aresta de 4px, a mesma gramática de
 * `calendar-compact-card.tsx`, onde a cor é aresta e nunca bloco.
 *
 * Há uma montagem só: o card sempre mede ~35-46% do palco que flutua sobre a
 * foto. Como o palco acompanha a largura da tela, no mobile ele chega a ~160px
 * — por isso tipografia e respiro encolhem por breakpoint, senão títulos longos
 * ("Consulta com Fonoaudióloga") truncam antes de o palco chegar ao desktop.
 */
export function EventCard({ title, timeRange, className, style }: EventCardProps) {
  return (
    <div
      className={cn(
        // O trilho lime é `inset` do próprio card, não um filho com
        // `overflow-hidden` por cima. Como filho ele era recortado pela curva
        // do `rounded-xl`, e a costura desse clip aparecia como um fio escuro
        // nas pontas da faixa. Sombra inset segue o raio sem recorte nenhum.
        "flex items-stretch rounded-lg md:rounded-xl bg-tami-dark",
        "shadow-[0_3px_11px_theme(colors.black/25%),inset_4px_0_0_theme(colors.tami.green)]",
        className,
      )}
      style={style}
    >
      {/* `pl-3`/`pl-4` e não `pl-2`/`pl-3`: os 4px do trilho inset comem o
          respiro da esquerda. */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-3 pr-2 py-1.5 md:gap-2 md:pl-4 md:pr-3 md:py-2.5">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate font-semibold leading-tight text-tami-green text-[8px] min-[360px]:text-[9px] md:text-[10px] lg:text-[12px]">
            {title}
          </span>
          <span className="leading-tight tabular-nums text-tami-light/85 text-[7px] min-[360px]:text-[8px] md:text-[9px] lg:text-[10.5px]">
            {timeRange}
          </span>
        </div>

        {/* Decorativo: o chevron do original, que sugere um item expansível. */}
        <ChevronDown className="shrink-0 text-tami-light/70 h-3 w-3 md:h-3.5 md:w-3.5" aria-hidden />
      </div>
    </div>
  );
}
